import React, { useEffect, useMemo, useState, type ReactNode } from 'react';
import NotificationsContext from './context';

import {
  DEVELOPER_ROLE,
  STATUS_PENDING,
} from '../../assets/AppConstants';

import { useAppContext } from '../app';
import { useUserContext } from '../user';
import { getNotificationsRefresh } from '../../services/Notifications';
import { useWorkspaceContext } from '../workspace';

// Local dev toggle (startup config, read by Vite at dev-server boot): notification polling is ON by default.
// Set VITE_NOTIFICATIONS_POLLING='FALSE' (e.g. in .env.local-be) to silence it locally — no initial fetch, no interval.
const NOTIFICATIONS_POLLING_ENABLED =
  String(import.meta.env.VITE_NOTIFICATIONS_POLLING ?? 'TRUE').toUpperCase() !== 'FALSE';

const NOTIFICATIONS_POLLING_INTERVAL_MS = 20000;
interface NotificationsContextProviderProps {
  children: ReactNode;
}

const NotificationsContextProvider = ({ children }: NotificationsContextProviderProps) => {
  const {
    userSession,
    userIsLogged,
    getUserStatus,
    switchingClient,
  } = useUserContext();

  const {
    hasRequestStatus,
  } = useAppContext();
  const {
    workspace: {
      defaultWorkspace,
      getWorkspaceRole,
    },
  } = useWorkspaceContext();
  const [newNotifications, setNewNotifications] = useState(0);

  const workspaceRole = getWorkspaceRole();
  // The session moves to the new client before its workspace loads.
  const scopeIsSessionClient = !switchingClient
    && Boolean(defaultWorkspace.clientId)
    && defaultWorkspace.clientId === userSession.clientId;
  const roleSeesNotifications = Boolean(workspaceRole) && workspaceRole !== DEVELOPER_ROLE;

  const canPoll = NOTIFICATIONS_POLLING_ENABLED
    && userIsLogged()
    && getUserStatus() !== STATUS_PENDING
    && scopeIsSessionClient
    && roleSeesNotifications
    && !hasRequestStatus();

  // The count is per workspace, so a workspace change refreshes it at once.
  useEffect(() => {
    if (!canPoll) return undefined;

    let active = true;
    const reloadNotifications = () => getNotificationsRefresh({ page: 0, onlyUnread: true })
      .then((unreadNotifications) => {
        if (active) setNewNotifications(unreadNotifications?.totalElements ?? 0);
      })
      .catch(() => undefined);

    reloadNotifications();
    const intervalId = setInterval(reloadNotifications, NOTIFICATIONS_POLLING_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [canPoll, defaultWorkspace.id]);

  const notificationsValue = useMemo(() => ({
    newNotifications,
    changeNewNotifications: setNewNotifications,
  }), [newNotifications]);

  return (
    <NotificationsContext.Provider value={notificationsValue}>
      {children}
    </NotificationsContext.Provider>
  );
};
export default NotificationsContextProvider;
